import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { ProcessingLoader } from '@/components/ProcessingLoader';
import { INVESTMENT_STRATEGIES } from '@/helpers/constants';
import { lockBodyScrollClass, unlockBodyScrollClass } from '@/helpers/scroll';
import { useRoundStatus } from '@/hooks/shared/useRoundStatus';
import { useSetInvestmentStrategy } from '@/query/queries/investing/useSetInvestmentStrategy';
import { BrandDetailModal } from '@/pages/BrandDetailPage/components/BrandDetailModal';
import { Preloader } from '@/components/Preloader';
import { EveryFiveGroupAModal } from '@/modals/EveryFiveGroupAModal';
import { generateUniqId } from '@/helpers/uuid';
import { useGetMe } from '@/hooks/useGetMe';
import { InlineAlert } from '@scramble/ui';
import { OffersBanner } from '@/containers/OffersBanner';
import { RoundInformationStatus } from './components/RoundInformation';
import { InvestingProgressBar } from './components/InvestingProgressBar';
import { InvestedCountries } from './components/InvestedCountries';
import { StepAllocation } from './components/StepAllocation';
import { StepReview } from './components/StepReview';
import { InvestmentBreakdown } from './components/InvestmentBreakdown';
import { EarlyReservationModerate } from './components/EarlyReservationModerate';
import { MyParticipation } from './components/MyParticipation';
import { ResetInvesting } from './components/ResetInvesting';
import { InvestedInGroupsStats } from './components/InvestedInGroupsStats';
import { BrandsParticipation } from './components/BrandsParticipation';
import { InvestingFaq } from './components/InvestingFaq';
import { ReviewParticipationModal } from '@/modals/ReviewParticipationModal';
import { InvestorDecisionWidget } from '@/containers/InvestorDecisionWidget';
import s from './index.module.scss';

const InvestingPage = () => {
  const location = useLocation();
  const { invest } = location.state || {};
  const { hasInvestingRestriction} = useGetMe();
  const [loading, setLoading] = useState(false);
  const [savedAllocation, setSavedAllocation] = useState(null);
  const wizardId = useRef(generateUniqId());
  const [allocationStep, setAllocationAllocationStep] = useState(
    invest ? 'start' : null,
  );
  const { t } = useTranslation('investing');
  const { status, isParticipating } = useRoundStatus();
  const { mutateAsync: setStrategy } = useSetInvestmentStrategy();

  useEffect(() => {
    allocationStep ? lockBodyScrollClass() : unlockBodyScrollClass();
    return () => {
      unlockBodyScrollClass();
    };
  }, [allocationStep]);

  const generateWizardId = () => {
    wizardId.current = generateUniqId();
  };

  const onJoinRound = async () => {
    if (
      !status?.option ||
      status?.option === INVESTMENT_STRATEGIES.not_selected
    ) {
      try {
        setLoading(true);
        await setStrategy({
          option: INVESTMENT_STRATEGIES.standard,
          founders: [],
        });
      } catch {
        return console.error('Could not set strategy');
      } finally {
        setLoading(false);
      }
    }
    setAllocationAllocationStep('start');
  };

  if (!status)
    return (
      <div className="container-inner">
        <Preloader style={{ minHeight: '50vh' }} />
      </div>
    );

  return (
    <div className={s.page}>
      <div className="g-container">
        <div className={s.content}>
          <h1 className={s.title}>{t('investing:page.title')}</h1>
          {hasInvestingRestriction && (
            <InlineAlert
              type="warning"
              title={t('investing:warning.investing_unavailable')}
              message={t('investing:warning.investing_under_review')}
            />
          )}
          <RoundInformationStatus onJoin={onJoinRound} />
          <EarlyReservationModerate />
          {isParticipating && <MyParticipation />}
          {isParticipating && <InvestmentBreakdown />}
          {isParticipating && <ResetInvesting />}
          <InvestingProgressBar />
          <OffersBanner />
          <InvestedInGroupsStats />
          <InvestedCountries />
          <BrandsParticipation />
          <InvestingFaq step={status.step} />
        </div>
        <>
          {allocationStep === 'start' && (
            <StepAllocation
              wizardId={wizardId.current}
              onNext={() => setAllocationAllocationStep('review')}
              onBack={() => {
                setAllocationAllocationStep(null);
                setSavedAllocation(null);
                generateWizardId();
              }}
              setLoading={setLoading}
              savedAllocation={savedAllocation}
              onAllocationSaved={values => setSavedAllocation(values)}
            />
          )}
          {allocationStep === 'review' && (
            <StepReview
              wizardId={wizardId.current}
              onSuccess={() => {
                setAllocationAllocationStep(null);
                setSavedAllocation(null);
                generateWizardId();
              }}
              onBack={() => setAllocationAllocationStep('start')}
              setLoading={setLoading}
            />
          )}
        </>

        <>
          <EveryFiveGroupAModal />
          <BrandDetailModal />
          <ReviewParticipationModal />
        </>
        <ProcessingLoader loading={loading} />
        {!allocationStep && <InvestorDecisionWidget placement="investing" />}
      </div>
    </div>
  );
};

export default InvestingPage;
